<template>
  <div class="chart-bar-horizontal" />
</template>

<script>
import * as d3 from 'd3';

import * as am4core from '@amcharts/amcharts4/core';
import * as am4charts from '@amcharts/amcharts4/charts';
import am4themesAnimated from '@amcharts/amcharts4/themes/animated';

am4core.useTheme(am4themesAnimated);

export default {
  name: 'BarChartY',
  props: {
    type: {
      type: String,
      required: true,
    },
  },
  watch: {
    type(newVal) {
      this.filterChart(newVal);
    },
  },
  mounted() {
    // load data
    const dataSource = new am4core.DataSource();
    dataSource.url = `${process.env.BASE_URL}data/bar-y.csv`;
    dataSource.parser = new am4core.CSVParser();
    dataSource.parser.options.emptyAs = '';
    dataSource.parser.options.useColumnNames = true;

    dataSource.load();

    dataSource.events.on('done', ({ data }) => {
      // sort type prop by alphabet
      const rawData = data.sort((a, b) => (a.type === b.type ? 0 : +(a.type > b.type) || -1));
      // set default "type"
      this.typeDefault = rawData[0].type;

      // prepare data
      this.sanitizedData = this.formulateData(rawData);

      // pass data to parent
      this.$emit('data-sanitized', rawData);
      this.$emit('update:type', this.typeDefault);

      // create chart
      this.chart = this.createChart();

      // create axes
      this.categoryAxis = this.createAxisY();
      this.valueAxis = this.createAxisX();

      // create series
      this.series = this.createSeries();

      // set chart default data
      this.setChartData(this.typeDefault);

      // this.chart.events.on('beforedatavalidated', (ev) => {
      //   console.log(ev.target.data);
      //   if (ev.target.data.length <= 0) {
      //     console.log('filtering');
      //   }
      // })

      // this.chart.series.events.on('inserted', (ev) => {
      //   console.log(ev);
      //   const currentSeries = this.sanitizedData.get(this.type);

      //   ev.newValue.data.forEach(d => {
      //     const { percentage } = currentSeries.find(newData => newData.category === d.category);
      //     d.percentage = percentage;
      //   });

      //   ev.newValue.invalidateRawData();
      //   console.log(this.sanitizedData);
      // })

      // set series
      // this.series = this.mapSeries();
      // this.series.forEach((value, key) => {
      //   const seriesData = this.sanitizedData.get(key);

      //   const test = seriesData.map(({ category }) => {
      //     return { category, percentage: 0};
      //   })

      //   // create series
      //   const series = this.createSeries(key, test);
      //   this.series.set(key, series);
      // });

      // // init series
      // this.setSeries(this.type);
    });
  },
  beforeDestroy() {
    if (this.chart) {
      this.chart.dispose();
    }
  },
  methods: {
    formulateData(rawData) {
      // convert percentage to Number
      const data = rawData.map(({ percentage, ...props }) => {
        return { ...props, percentage: Number(percentage) };
      });

      // group by type
      const dataByType = d3.group(data, (d) => d.type);

      // convert Map to Array
      const dataByTypeArr = Array.from(dataByType, ([key, value]) => ({ key, value }));

      // sort the data
      dataByTypeArr.forEach((el) => {
        // sort by percentage
        el.value.sort((a, b) => d3.descending(a.percentage, b.percentage));

        // sort "Others" to the end
        el.value.sort((a, b) => (a.category === 'Others') - (b.category === 'Others'));
      });

      // convert back to Map
      const sanitizedData = new Map(dataByTypeArr.map((item) => [item.key, item.value]));

      return sanitizedData;
    },
    createChart() {
      // create XY chart
      const chart = am4core.create(this.$el, am4charts.XYChart);

      // set initial chart data
      // chart.data = this.sanitizedData.get(this.type);

      // settings
      chart.padding(4, 18, 0, 10);
      chart.zoomOutButton.disabled = true;

      return chart;
    },
    createAxisY() {
      // y-axis - category axis
      const categoryAxis = this.chart.yAxes.push(new am4charts.CategoryAxis());
      // logic settings
      categoryAxis.dataFields.category = 'category';
      // categoryAxis.sortBySeries = this.series;
      categoryAxis.renderer.inversed = true;

      // style settings
      categoryAxis.renderer.grid.template.disabled = true;
      categoryAxis.renderer.grid.template.location = 1;
      // cell width
      categoryAxis.renderer.cellStartLocation = 0.2;
      categoryAxis.renderer.cellEndLocation = 0.8;
      // labels
      categoryAxis.renderer.inside = true;
      categoryAxis.renderer.labels.template.location = 0;
      categoryAxis.renderer.labels.template.dy = 3;
      categoryAxis.renderer.labels.template.dx = -5;
      categoryAxis.renderer.labels.template.fontSize = 14;
      categoryAxis.renderer.labels.template.fill = '#121212';

      if (this.$breakpoint.mobile) {
        categoryAxis.renderer.labels.template.dy = 7;
        categoryAxis.renderer.labels.template.fontSize = 12;
      }

      return categoryAxis;
    },
    createAxisX() {
      // x-axis - value axis
      const valueAxis = this.chart.xAxes.push(new am4charts.ValueAxis());
      // logic settings
      valueAxis.min = 0;
      valueAxis.extraMax = 0.1;
      // style settings
      valueAxis.renderer.labels.template.adapter.add('text', (label) => {
        return `${label}%`;
      });
      valueAxis.renderer.grid.template.stroke = '#F0F0F0';
      valueAxis.renderer.grid.template.strokeOpacity = 1;
      valueAxis.renderer.labels.template.fontSize = 12;
      valueAxis.renderer.labels.template.fill = '#595959';

      if (this.$breakpoint.mobile) {
        valueAxis.renderer.labels.template.fontSize = 11;
      }

      return valueAxis;
    },
    createSeries() {
      const series = this.chart.series.push(new am4charts.ColumnSeries());
      // const series = new am4charts.ColumnSeries();

      // attach data
      // series.data = data;

      // series settings
      // series.name = key;
      series.dataFields.valueX = 'percentage';
      series.dataFields.categoryY = 'category';
      series.columns.template.fill = '#005EB8';
      series.columns.template.stroke = '#005EB8';

      // create value label bullets
      const valueLabel = series.bullets.push(new am4charts.LabelBullet());
      valueLabel.label.text = '{percentage}%';
      valueLabel.label.truncate = false;
      valueLabel.label.hideOversized = false;
      valueLabel.label.horizontalCenter = 'left';
      valueLabel.label.dx = 10;
      valueLabel.label.fontSize = 14;
      valueLabel.label.fontWeight = 500;
      valueLabel.label.fill = '#121212';

      return series;
    },
    mapSeries() {
      const seriesSet = [...this.sanitizedData.keys()].map((key) => [key, null]);

      return new Map(seriesSet);
    },
    setChartData(type) {
      // get data set by type
      const selectedSet = this.sanitizedData.get(type);
      const selectedData = selectedSet.map(({ category, percentage }) => {
        return { category, percentage };
      });

      // calculate data count difference
      const newDataCount = selectedData.length - this.series.data.length;

      // set category data
      this.categoryAxis.data = selectedSet.map(({ category }) => {
        return { category };
      });

      // set series data
      if (this.series.data.length <= 0) {
        // set inital data
        this.series.data = selectedData;
      } else {
        // check if add or remove data
        if (newDataCount < 0) {
          this.series.removeData(Math.abs(newDataCount));
        } else {
          // create new empty data by count
          const newEmptyData = [...new Array(newDataCount)].map(() => {
            return { category: '', percentage: 0 };
          });

          this.series.addData(newEmptyData);
        }

        // update data values
        this.series.data.forEach((d, i) => {
          const { category, percentage } = selectedSet.find((newData, newIdx) => i === newIdx);

          d.category = category;
          d.percentage = percentage;
        });
      }
    },
    filterChart(type) {
      this.setChartData(type);
      this.series.invalidateRawData();

      // this.chart.data = [];
      // this.chart.data = selectedSet;
      // this.categoryAxis.removeChildren();
      // if (this.chart.isDisposed()) {
      //   // create chart
      //   this.chart = this.createChart();

      //   // create axes
      //   this.categoryAxis = this.createAxisY();
      //   this.valueAxis = this.createAxisX();

      //   // create series
      //   this.series = this.createSeries();
      // }
      // this.categoryAxis = this.createAxisY();

      // if same category, update its value
      // this.chart.data.forEach((d, i) => {
      //   if (d.category === 'Others') {
      //     const { percentage } = selectedSet.find(newData => newData.category === d.category);
      //     d.percentage = percentage;
      //   } else {
      //     const test = selectedSet.find((newData, idx) => idx === i);
      //     // console.log(test.category);
      //     d.category = test.category;
      //     d.percentage = test.percentage;
      //   }
      //   // if (selectedSet.some(newData => newData.category === 'Others')) {
      //   //   const { percentage } = selectedSet.find(newData => newData.category === d.category)
      //   //   d.percentage = percentage;
      //   // } else {
      //   //   // console.log(d, i);
      //   //   const test = selectedSet.find((newData, newDataIndex) => newDataIndex === i);
      //   //   d.percentage = test.percentage;
      //   // }
      // });

      // this.chart.invalidateRawData();
      // this.chart.series.getIndex(0).invalidateRawData();
      // console.log(this.categoryAxis);

      // this.categoryAxis.data = this.sanitizedData.get(type).map(({ category }) => {
      //   return { category };
      // });

      // this.categoryAxis.dataFields.category = 'ddd';
      // console.log(this.categoryAxis);
      // this.chart.series.sort();

      // this.chart.setState('hidden')
      // this.chart.addData(data);
      // this.categoryAxis.data = this.sanitizedData.get(type).map(({ category }) => {
      //   return { category };
      // });
      // // this.setSeries(type);
      // this.chart.series.getIndex(0).data.forEach((d, i) => {
      //   d.category = this.sanitizedData.get(type).find((dd, ii) => i === ii).category;
      //   d.percentage = this.sanitizedData.get(type).find((dd, ii) => i === ii).percentage;
      // })
      // this.chart.series.getIndex(0).invalidateRawData();
      // console.log(this.chart.yAxes);
      // console.log(this.chart.seriesContainer);
      // this.chart.series.setState('default');
      // get data by selected type
      // const selectedSet = this.sanitizedData.get(type);

      // // update chart data
      // this.chart.data = selectedSet;
    },
  },
};
</script>

<style lang="scss" scoped>
.chart-bar-horizontal {
  width: 100%;
  height: 400px;
}
</style>
